/** Können-Check — where the learner stands, and what to do next.
 *
 * Two rules shape this component, both learned the hard way on the
 * practice readiness score:
 *
 *  1. A skill with no earned score shows what is MISSING ("4 more tasks"),
 *     never a provisional number. A figure a learner might act on has to
 *     be earned first.
 *  2. The next action comes FIRST and there are at most three. Six bars
 *     tell someone they are behind; one sentence tells them what to do
 *     about it tonight.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../../api/reference";
import type { KoennenCheck as Report, NextAction, SkillScore } from "../../api/reference";
import { Card, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

const BAND_CLASS: Record<string, string> = {
  needsWork: "text-danger",
  onTrack: "text-warning",
  strong: "text-success",
};

/** Where a given skill is actually practised. */
const SKILL_LINK: Record<string, string> = {
  wortschatz: "/reference?tab=review",
  grammatik: "/learn",
  lesen: "/practice",
  hoeren: "/practice",
  schreiben: "/practice",
  sprechen: "/speak",
};

function SkillRow({ skill }: { skill: SkillScore }) {
  const t = useT();
  const label = t(`skill.${skill.skill}` as never);
  const pct = skill.score ?? 0;

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold text-ink">{label}</span>
        {skill.state === "earned" ? (
          <span className={`font-bold ${BAND_CLASS[skill.band ?? "onTrack"]}`}>
            {skill.score}%
          </span>
        ) : (
          <span className="text-xs text-muted">
            {skill.state === "none"
              ? t("kc.notStarted")
              : t("kc.needMore", { count: skill.needed - skill.evidence })}
          </span>
        )}
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-line">
        <div
          className={`h-full rounded-full ${
            skill.state === "earned" ? "bg-brand" : "bg-line"
          }`}
          style={{ width: `${skill.state === "earned" ? pct : 0}%` }}
        />
      </div>
    </li>
  );
}

function ActionRow({ action }: { action: NextAction }) {
  const t = useT();
  const skill = t(`skill.${action.skill}` as never);
  const text = t(`kc.action.${action.kind}` as never, {
    skill,
    count: action.count,
  });
  return (
    <li>
      <Link
        to={SKILL_LINK[action.skill] ?? "/learn"}
        className="flex items-center justify-between gap-3 rounded-control border border-line px-3 py-2 text-sm hover:border-brand"
      >
        <span className="text-ink">{text}</span>
        <span aria-hidden className="text-muted">→</span>
      </Link>
    </li>
  );
}

export function KoennenCheck({ level }: { level?: string }) {
  const t = useT();
  const [report, setReport] = useState<Report | null>(null);

  useEffect(() => {
    api.koennenCheck(level).then(setReport).catch(() => setReport(null));
  }, [level]);

  if (!report) return <Spinner />;

  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xl font-bold text-ink">{t("kc.title")}</h2>
        {report.overall !== null ? (
          <span className={`text-2xl font-extrabold ${BAND_CLASS[report.overall_band ?? "onTrack"]}`}>
            {report.overall}%
          </span>
        ) : (
          <span className="text-xs text-muted">
            {t("kc.overallLocked", { count: 4 - report.earned_count })}
          </span>
        )}
      </div>

      {report.next_actions.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t("kc.doNext")}
          </p>
          <ul className="mt-2 flex flex-col gap-2">
            {report.next_actions.map((a) => (
              <ActionRow key={`${a.kind}-${a.skill}`} action={a} />
            ))}
          </ul>
        </div>
      )}

      <ul className="mt-5 flex flex-col gap-3">
        {report.skills.map((s) => (
          <SkillRow key={s.skill} skill={s} />
        ))}
      </ul>
    </Card>
  );
}
